class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }
  
  setupEventListeners() {
    if (this.input?.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if (this.input) {
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || (newSearchTerm && !newSearchTerm.startsWith(this.searchTerm))) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (!this.input || !event.relatedTarget) return;
      const isClickInsideInput = this.input.contains(event.relatedTarget);
      if (!isClickInsideInput) {
        return;
      }

      if (!this.contains(document.activeElement)) {
        return;
      }

      this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const regex = new RegExp(this.escapeRegExp(previousTerm), 'g');
      const matches = currentButtonText.match(regex);
      if (matches && matches.length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null  
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    if (!searchTerm) return; 
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // ===== 新增：根据搜索词匹配变体并更新链接 =====
    this.updateProductLinksWithVariants();
    // ==========================================

    this.setLiveRegionResults();
    this.open();
  }

  // ===== 新增方法：更新产品链接以包含匹配的变体 =====
  updateProductLinksWithVariants() {
    const searchKeywords = this.searchTerm.toLowerCase().split(' ').filter(k => k.length > 0);
    const productItems = this.predictiveSearchResults.querySelectorAll('.predictive-search__list-item[data-variants]');

    productItems.forEach(item => {
      try {
        const variantsJson = item.getAttribute('data-variants');
        if (!variantsJson) return;

        const variants = JSON.parse(variantsJson);
        if (!variants || variants.length === 0) return;

        const productLink = item.querySelector('a');
        if (!productLink) return;

        // 查找最匹配的变体
        const matchedVariant = this.findBestMatchingVariant(variants, searchKeywords);

        if (matchedVariant && matchedVariant.id) {
          // 更新链接，添加 variant 参数
          const url = new URL(productLink.href, window.location.origin);
          url.searchParams.set('variant', matchedVariant.id);
          productLink.href = url.toString();

          // 可选：添加 data 属性用于调试
          item.setAttribute('data-matched-variant-id', matchedVariant.id);
          item.setAttribute('data-matched-variant-title', matchedVariant.title);
        }
      } catch (e) {
        console.warn('Error processing product variants:', e);
      }
    });
  }

  // 查找最佳匹配的变体
  findBestMatchingVariant(variants, searchKeywords) {
    let bestMatch = null;
    let highestScore = 0;

    variants.forEach(variant => {
      const variantTitle = (variant.title || '').toLowerCase();
      const variantOption1 = (variant.option1 || '').toLowerCase();
      const variantOption2 = (variant.option2 || '').toLowerCase();
      const variantOption3 = (variant.option3 || '').toLowerCase();
      
      let score = 0;

      searchKeywords.forEach(keyword => {
        // 完全匹配变体标题 - 最高分
        if (variantTitle === keyword) {
          score += 20;
        } else if (variantTitle.includes(keyword)) {
          score += 10;
        }

        // 匹配单个选项值
        if (variantOption1.includes(keyword)) score += 8;
        if (variantOption2.includes(keyword)) score += 8;
        if (variantOption3.includes(keyword)) score += 8;

        // 匹配变体标题的各个部分（用 - 分隔）
        const titleParts = variantTitle.split('-').map(p => p.trim());
        titleParts.forEach(part => {
          if (part === keyword) {
            score += 15;
          } else if (part.includes(keyword)) {
            score += 5;
          }
        });

        // 部分匹配（关键词是变体标题某部分的开头）
        if (titleParts.some(part => part.startsWith(keyword))) {
          score += 3;
        }
      });

      // 如果所有搜索关键词都在变体中找到，额外加分
      const allKeywordsFound = searchKeywords.every(keyword => 
        variantTitle.includes(keyword) || 
        variantOption1.includes(keyword) || 
        variantOption2.includes(keyword) || 
        variantOption3.includes(keyword)
      );
      
      if (allKeywordsFound) {
        score += 25;
      }

      if (score > highestScore) {
        highestScore = score;
        bestMatch = variant;
      }
    });

    // 如果没有找到匹配，返回第一个可用的变体
    return bestMatch || variants.find(v => v.available) || variants[0];
  }
  // ==========================================

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
